import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Smartphone,
  Tablet,
  Monitor,
  Globe,
  MoreVertical,
  Undo,
  Redo,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@mawkaa/ui';
import { useBuilderStore } from '@/stores/builderStore';
import { cn } from '@mawkaa/ui';

interface BuilderLayoutProps {
  children: React.ReactNode;
}

export function BuilderLayout({ children }: BuilderLayoutProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    site,
    isPreviewMode,
    isSaving,
    hasUnsavedChanges,
    devicePreview,
    setPreviewMode,
    setDevicePreview,
    savePage,
    publishSite,
    unpublishSite,
  } = useBuilderStore();

  const handleSave = async () => {
    try {
      await savePage();
      toast({ title: 'Sauvegardé', description: 'Vos modifications ont été enregistrées.' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder les modifications.',
        variant: 'destructive',
      });
    }
  };

  const handlePublish = async () => {
    try {
      if (hasUnsavedChanges) {
        await savePage();
      }
      const url = await publishSite();
      toast({
        title: 'Site publié !',
        description: `Votre site est maintenant accessible sur ${url}`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de publier le site.',
        variant: 'destructive',
      });
    }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishSite();
      toast({ title: 'Site dépublié', description: 'Votre site n\'est plus accessible.' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de dépublier le site.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Top toolbar */}
      <header className="h-14 bg-white border-b flex items-center justify-between px-4">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/sites"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour</span>
          </Link>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">M</span>
            </div>
            <span className="font-medium">{site?.name || 'Chargement...'}</span>
            {hasUnsavedChanges && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                Non sauvegardé
              </span>
            )}
          </div>
        </div>

        {/* Center section - Device preview */}
        <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setDevicePreview('desktop')}
            className={cn(
              'p-2 rounded transition-colors',
              devicePreview === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
            )}
            title="Desktop"
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDevicePreview('tablet')}
            className={cn(
              'p-2 rounded transition-colors',
              devicePreview === 'tablet' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
            )}
            title="Tablet"
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDevicePreview('mobile')}
            className={cn(
              'p-2 rounded transition-colors',
              devicePreview === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
            )}
            title="Mobile"
          >
            <Smartphone className="w-4 h-4" />
          </button>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPreviewMode(!isPreviewMode)}
            title={isPreviewMode ? 'Mode édition' : 'Aperçu'}
          >
            {isPreviewMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges}
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm">
                <Globe className="w-4 h-4 mr-2" />
                {site?.status === 'published' ? 'Publié' : 'Publier'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {site?.status !== 'published' ? (
                <DropdownMenuItem onClick={handlePublish}>
                  <Globe className="w-4 h-4 mr-2" />
                  Publier le site
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={handlePublish}>
                    <Globe className="w-4 h-4 mr-2" />
                    Mettre à jour
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.open(`https://${site.subdomain}.mawkaa.tn`, '_blank')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Voir le site
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleUnpublish} className="text-red-600">
                    <EyeOff className="w-4 h-4 mr-2" />
                    Dépublier
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}
